import type { PayableInvoice } from '../../types/payments';
import { formatCurrency } from '../../lib/format';
import { formatPropertyLabel } from '../../lib/payments';
import type { RunSuccess } from '../../hooks/usePaymentRun';

interface RunSummaryProps {
  outcome: 'completed' | 'stopped' | 'halted';
  successes: RunSuccess[];
  /** Total spent, per currency. */
  totals: Record<string, number>;
  /** Set when the run was stopped with an attempt still awaiting approval. */
  inFlight?: { invoice: PayableInvoice; attemptId: string } | null;
  /** Why the run halted — shown only for the halted outcome. */
  haltReason?: string | null;
  /**
   * Back to the (re-fetched) queue. Omitted for halts that render an
   * AttemptOutcome below the summary — that component carries the action.
   */
  onBack?: () => void;
}

const OUTCOME_STYLES = {
  completed: { border: 'border-green-200', heading: 'text-green-800', title: 'Run completed' },
  stopped: { border: 'border-amber-200', heading: 'text-amber-800', title: 'Run stopped' },
  halted: { border: 'border-red-200', heading: 'text-red-800', title: 'Run halted' },
} as const;

/**
 * End-of-run summary (CHO-762) for completed, stopped, and halted runs: what
 * was paid, the per-currency totals, and — for halts — why. There is no retry
 * affordance anywhere; a stopped or halted run is resumed by starting a new
 * one, which re-snapshots eligibility. A stop with an attempt still in flight
 * says so in plain words: approving it later on the phone completes it
 * server-side, and there is no rollback of what already paid.
 */
export function RunSummary({ outcome, successes, totals, inFlight = null, haltReason = null, onBack }: RunSummaryProps) {
  const styles = OUTCOME_STYLES[outcome];
  return (
    <div className={`bg-white border ${styles.border} rounded-lg p-6 max-w-2xl`}>
      <h2 className={`text-lg font-semibold ${styles.heading}`}>{styles.title}</h2>

      {outcome === 'completed' && (
        <p className="text-sm text-gray-700 mt-1">
          All {successes.length} invoice{successes.length === 1 ? '' : 's'} were paid and recorded.
        </p>
      )}

      {outcome === 'stopped' && (
        <p className="text-sm text-gray-700 mt-1">
          You stopped the run. Everything already paid stays paid — nothing is rolled back.
        </p>
      )}

      {outcome === 'halted' && haltReason && (
        <p role="alert" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {haltReason}
        </p>
      )}

      {inFlight && (
        <p role="alert" className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          You stopped while {formatPropertyLabel(inFlight.invoice)} — {inFlight.invoice.biller ?? '—'} (
          {formatCurrency(inFlight.invoice.amount, inFlight.invoice.currency)}) was still awaiting your approval. That
          attempt is live and cannot be recalled: if you approve it on your phone later, it completes. The invoice stays
          ineligible — “Already attempted” — until the attempt resolves.
        </p>
      )}

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-400">Paid ({successes.length})</h3>
          {successes.length === 0 ? (
            <p className="text-sm text-gray-400 mt-2">Nothing was paid in this run.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-sm text-gray-700">
              {successes.map((success) => (
                <li key={success.attemptId} className="flex justify-between gap-3">
                  <span>
                    {formatPropertyLabel(success.invoice)} — {success.invoice.biller ?? '—'}
                  </span>
                  <span className="whitespace-nowrap">{formatCurrency(success.amount, success.currency)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-400">Total spent</h3>
          {Object.keys(totals).length === 0 ? (
            <p className="text-sm text-gray-400 mt-2">—</p>
          ) : (
            <ul className="mt-2 space-y-1 text-sm text-gray-700">
              {Object.entries(totals).map(([currency, amount]) => (
                <li key={currency} className="flex justify-between gap-3">
                  <span className="text-gray-500">{currency}</span>
                  <span className="font-medium text-gray-900">{formatCurrency(amount, currency)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mt-6 px-4 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-gray-700"
        >
          Back to queue
        </button>
      )}
    </div>
  );
}
